<script setup>
import Accordion from "primevue/accordion";
import AccordionTab from "primevue/accordiontab";
import InputSwitch from "primevue/inputswitch";
import Tooltip from "@/components/shared/Tooltip/PrimeVueTooltip.vue";
import { computed, defineProps, ref, watch } from "vue";
import AutoComplete from "primevue/autocomplete";
import { useDebounce, useVModel } from "@vueuse/core";
import { sessionStatus } from "@/components/program-manager/sessions/constants";
import { usePrimeVue } from "primevue/config";
import { inputSwitchPt, autoCompletePt, selectButtonPt } from "./style";
import { useGetEntitiesData } from "../../composable";
import { hasProgramsWritePermission, hasProgramsPublishPermission, hasEnableShareOptionsEnabled } from "@/composables/authorization";
import SelectButton from "primevue/selectbutton";

defineOptions({
  name: "PromotionalSettings",
});

const props = defineProps({
  modelValue: {
    type: Object,
    required: false,
    default: () => {},
  },
});
const { config } = usePrimeVue();

const disabledStatus = [sessionStatus.completed, sessionStatus.cancelled];
const publishStatus = [sessionStatus.openEnrollment, sessionStatus.inSession];

const disableToggles = computed(() => {
  return (
    disabledStatus.includes(programSession.value.status_id) ||
    (programSession.value.status_id == sessionStatus.draft && !hasProgramsWritePermission()) ||
    (publishStatus.includes(programSession.value.status_id) && !hasProgramsPublishPermission())
  );
});

const emit = defineEmits(["update:modelValue"]);
const programSession = useVModel(props, "modelValue", emit);

const searchText = ref("");
const debouncedSearch = useDebounce(searchText, 500);

const onSearchComplete = (event) => {
  searchText.value = event.query;
};

const { data: entitiesList } = useGetEntitiesData({ limit: 10, page: 1, q: debouncedSearch });

const sharedWithIds = computed(() => {
  return programSession?.value?.shared_with_entities?.map((item) => item?.id);
});

watch(sharedWithIds, () => {
  programSession.value.shared_with = sharedWithIds.value;
});

const filteredSuggestions = computed(() =>
  entitiesList?.value?.filter(
    (item) => !programSession?.value?.shared_with_entities?.some((selectedItem) => selectedItem.id === item.id)
  )
);

const shareOptions = [
  { label: "Public", value: true },
  { label: "Private", value: false },
];
</script>

<template>
  <Accordion :active-index="0">
    <AccordionTab header="Promotional Settings" :pt="{ root: { 'data-testid': 'promotional-settings-section' } }">
      <div v-if="hasEnableShareOptionsEnabled" class="grid grid-cols-1 md:grid-cols-5 gap-x-4 gap-y-4">
        <div class="md:col-span-1">
          <div class="flex flex-col mt-2 relative inline-flex w-auto">
            <label
              class="block text-sm font-semibold text-gray-700"
              data-testid="session-show-mobile-app-promotions-label"
            >
              Share Options
              <Tooltip message="Make this session public to share with all APIConnect users, or select partners." />
            </label>
            <SelectButton
              v-model="programSession.marketplace"
              :options="shareOptions"
              optionLabel="label"
              optionValue="value"
              :pt="selectButtonPt()"
            />
          </div>
        </div>
        <div class="md:col-span-4" v-if="!programSession.marketplace">
          <div class="flex flex-col mt-2 relative">
            <label
              class="block text-sm font-semibold text-gray-700"
              data-testid="session-show-mobile-app-promotions-label"
            >
              Share with
            </label>
            <AutoComplete
              v-model="programSession.shared_with_entities"
              :suggestions="filteredSuggestions"
              field="name"
              multiple
              @complete="onSearchComplete"
              :pt="autoCompletePt()"
            />
          </div>
        </div>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-4">
        <div class="flex flex-col mt-2 relative" v-if="hasEnableShareOptionsEnabled">
          <label
            class="block text-sm font-semibold text-gray-700"
            data-testid="session-show-mobile-app-promotions-label"
          >
            Hide from Search
            <Tooltip
              message="When 'Hide from Search' is enabled, participants can only view this session via direct link"
            />
          </label>
          <div class="flex flex-row items-center mt-2 -my-2">
            <InputSwitch
              v-model="programSession.hide_from_search"
              class="flex-shrink-0"
              :pt="inputSwitchPt(config.pt.inputswitch.slider)"
              :disabled="disableToggles"
            />
            <p v-if="programSession.hide_from_search" class="text-xs pl-2">
              This session is only accessible via direct link
            </p>
          </div>
        </div>
        <div class="flex flex-col mt-2 relative">
          <label
            class="block text-sm font-semibold text-gray-700"
            data-testid="session-show-mobile-app-promotions-label"
          >
            Show Mobile App Promotions
            <Tooltip
              message="When enabled, the mobile app download banner appears on web; when disabled, is is hidden."
            />
          </label>
          <div class="flex flex-row items-center mt-2 -my-2">
            <InputSwitch
              v-model="programSession.show_mobile_app_promotions"
              data-testid="session-show-mobile-app-promotions-toggle"
              :pt="inputSwitchPt(config.pt.inputswitch.slider)"
              :disabled="disableToggles"
            />
          </div>
        </div>
        <div class="flex flex-col mt-2 relative">
          <label class="block text-sm font-semibold text-gray-700" data-testid="session-show-orientation-label">
            Show Orientation
            <Tooltip
              message="When enabled, the orientation widget is visible on mobile; when disabled, it is hidden."
            />
          </label>
          <div class="flex flex-row items-center mt-2 -my-2">
            <InputSwitch
              v-model="programSession.show_orientation"
              data-testid="session-show-orientation-toggle"
              :pt="inputSwitchPt(config.pt.inputswitch.slider)"
              :disabled="disableToggles"
            />
          </div>
        </div>
      </div>
    </AccordionTab>
  </Accordion>
</template>
